import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { FormWrapper, Input } from '@amzn/stencil-react-components/form';
import { Col } from '@amzn/stencil-react-components/layout';
import { MessageBannerType } from '@amzn/stencil-react-components/message-banner';
import { ModalContent, WithModal } from '@amzn/stencil-react-components/modal';
import { Label } from '@amzn/stencil-react-components/text';
import React, { useContext, useState } from 'react';
import CreateOrUpdateQueuesBuilder from 'src/builders/CreateOrUpdateQueuesBuilder';
import GetQueuesBuilder from 'src/builders/GetQueuesBuilder';
import { UserContext } from 'src/components/Authentication/Authentication';
import { SUCCESS_MSG_BANNER_AUTO_DISMISS_TIME } from 'src/constants/DisplayConstants';
import { useInlineError } from 'src/hooks/useInlineError';
import { Queue } from 'src/models/Queue';
import { QueueUserOperationType } from 'src/models/ServiceRequests/CaseAuthorityServiceRequests';

interface AddSystemUserToQueueModalProps {
  domainId: string;
  /**
   * When set (per-row usage), the queue identity is taken from the list and
   * no queue inputs are shown. When absent (header usage), the operator types
   * the queueId — covering Maple-created queues not yet backfilled into DDB.
   */
  queue?: Queue;
  onTableReload: Function;
  setMessageBannerDetails: Function;
  flipBannerVisibility: Function;
}

export const AddSystemUserToQueueModal = ({
  domainId,
  queue,
  onTableReload,
  setMessageBannerDetails,
  flipBannerVisibility
}: AddSystemUserToQueueModalProps) => {
  const userContext = useContext(UserContext).authenticatedUser;
  const [queueIdInput, setQueueIdInput] = useState('');
  const [queueNameInput, setQueueNameInput] = useState('');
  const [systemUserIdInput, setSystemUserIdInput] = useState('');
  const inlineError = useInlineError();

  const resetForm = () => {
    setQueueIdInput('');
    setQueueNameInput('');
    setSystemUserIdInput('');
  };

  /**
   * Resolves the queue this add targets. Row mode: use the queue as-is.
   * Typed mode: look it up so an existing queue keeps its stored name
   * (editQueue would otherwise rename it); only fall back to the typed name
   * when the queue is not in DDB yet (Maple backfill case).
   */
  const resolveQueue = async (): Promise<Queue | undefined> => {
    if (queue) {
      return queue;
    }
    const queueId = queueIdInput.trim();
    if (!queueId) {
      inlineError.setError('Please enter the queue ID.');
      return undefined;
    }
    let existing;
    try {
      existing = await GetQueuesBuilder.getQueues(domainId, queueId);
    } catch (error) {
      inlineError.showError(
        `Failed to look up queue '${queueId}' in ${domainId}. The system user was not added; retry or check the queue ID.`,
        error
      );
      return undefined;
    }
    if (existing.queues && existing.queues.length > 0) {
      return existing.queues[0];
    }
    const queueName = queueNameInput.trim();
    if (!queueName) {
      inlineError.setError(
        'Queue is not in DDB yet — enter the queue name exactly as created in Maple so the record can be backfilled.'
      );
      return undefined;
    }
    return { queueId, name: queueName } as Queue;
  };

  const addSystemUser = async (close) => {
    inlineError.clearError();
    const systemUserId = systemUserIdInput.trim();
    if (!systemUserId) {
      inlineError.setError('Please enter the system user ID.');
      return;
    }
    try {
      const targetQueue = await resolveQueue();
      if (!targetQueue) {
        return;
      }
      // CADS validates the userId against the {domain}-USERS table before any
      // mapping is written, so no client-side user lookup is needed.
      await CreateOrUpdateQueuesBuilder.editQueue(domainId, userContext.alias, {
        ...targetQueue,
        userOperation: {
          type: QueueUserOperationType.ADD,
          userIds: [systemUserId]
        }
      });
      onTableReload();
      setMessageBannerDetails({
        messageBannerText: `Successfully added system user ${systemUserId} to queue ${targetQueue.queueId}`,
        messageBannerType: MessageBannerType.Success,
        messageBannerAutoDismiss: SUCCESS_MSG_BANNER_AUTO_DISMISS_TIME
      });
      flipBannerVisibility();
      close();
      resetForm();
    } catch (error) {
      inlineError.showError(
        `Failed to add system user '${systemUserId}' to the queue. Check that this exact userId exists in the ${domainId} Users table.`,
        error
      );
    }
  };

  const renderModal = ({ close }) => (
    <ModalContent
      maxWidth="90vw"
      titleText={
        queue
          ? `Add System User to ${queue.name}`
          : 'Add System User by Queue ID'
      }
      buttons={[
        <Button
          type={'button'}
          onClick={(event) => {
            event?.preventDefault();
            void addSystemUser(close);
          }}
          variant={ButtonVariant.Primary}
        >
          Add System User
        </Button>
      ]}
    >
      <Col gridGap={'S300'} width={600}>
        <FormWrapper width={600}>
          {!queue && (
            <>
              <Label htmlFor="queue-id-input">
                Queue ID (must exactly match the ID of the queue created in
                Maple)
              </Label>
              <Input
                id="queue-id-input"
                value={queueIdInput}
                onChange={(event) => setQueueIdInput(event.target.value)}
              />
              <Label htmlFor="queue-name-input">
                Queue Name (required only when the queue is not yet listed on
                this page — used to backfill the record)
              </Label>
              <Input
                id="queue-name-input"
                value={queueNameInput}
                onChange={(event) => setQueueNameInput(event.target.value)}
              />
            </>
          )}
          <Label htmlFor="system-user-id-input">
            System user ID (exact userId in the {domainId} Users table, e.g.
            systemUser{domainId}, falconSystemUser, CMS_USER — only system users
            may be added for Maple-migrated domains)
          </Label>
          <Input
            id="system-user-id-input"
            value={systemUserIdInput}
            onChange={(event) => setSystemUserIdInput(event.target.value)}
          />
        </FormWrapper>
        {inlineError.renderError(
          'AddSystemUserToQueueModal - addSystemUser',
          'Failed to add system user to the queue.'
        )}
      </Col>
    </ModalContent>
  );

  return (
    <WithModal
      renderModal={renderModal}
      isScrollable={false}
      onClose={resetForm}
    >
      {({ open }) => (
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={() => {
            inlineError.clearError();
            open();
          }}
        >
          {queue ? 'Add System User' : 'Add System User by Queue ID'}
        </Button>
      )}
    </WithModal>
  );
};
